import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTransition, useChain, animated, useSpringRef } from '@react-spring/web';
import { getMentorConnect } from '../../../../api/mentor_connect.js';
import { getMentorConnectExport } from '../../../../api/mentor_connect.js';

import ExportButton from '../../../ui_kit/buttons/export_button';
import NewPatientMenu from '../new_patient_menu';
import Context from '../../../../utils/context';
import Skeleton from '../../../ui_kit/utilities/skeleton';
import StatusMessage from '../../../ui_kit/utilities/status_message';
import TableWithPagination from '../../../ui_kit/tables';

import styles from './index.module.css';

export default function MentorConnect() {
  const [view, setView] = useState('loading');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState(null);
  const [users, setUsers] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    currentPage: 0,
    totalCount: 0,
    perPage: 10,
  });
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const isMobile = context.viewport.getIsMobile();
  const sortableHeaders = ['Sign up date', 'Initiated conversations', 'Completed conversations', 'Ready for next step'];
  const headers = [
    'Patient name',
    'Sign up date',
    ...(currentUser.is_reactiv8 || currentUser.is_medtronic ? ['Zipcode'] : []),
    'Initiated conversations',
    'Completed conversations',
    'Ready for next step',
  ];

  const keys = [
    'name',
    'created_at',
    ...(currentUser.is_reactiv8 || currentUser.is_medtronic ? ['zipcode'] : []),
    'initiated_conversations_count',
    'completed_conversations_count',
    'ready_for_next_step',
  ];

  const tableSkeletonAnimation = useSpringRef();
  const tableSkeleton = useTransition(view === 'loading' ? Array(10).fill({}) : users, {
    ref: tableSkeletonAnimation,
    trail: 500 / 15,
    config: { mass: 0.5, tension: 200, friction: 20 },
    from: { opacity: 0, transform: 'translateX(50px)' },
    enter: { opacity: 1, transform: 'translateX(0px)', position: 'relative' },
  });

  useChain([tableSkeletonAnimation]);

  const fetchUsers = useCallback(
    (page = 1, limit = 10, search = activeSearchTerm, sort_by = sortBy, sort_order = sortOrder) => {
      setView('loading');
      new Promise((resolve) => {
        const params = {
          page,
          limit,
        };
        if (search) {
          params.search_term = search;
        }
        if (sort_by) {
          params.sort_by = sort_by;
          params.sort_order = sort_order;
        }
        getMentorConnect(params, (usersArr, meta) => {
          setUsers(usersArr);
          if (meta && meta.pagination) {
            setPaginationMeta({
              currentPage: meta.pagination.current_page - 1, // MUI uses 0-indexed pages
              totalCount: meta.pagination.total_count,
              perPage: meta.pagination.per_page,
            });
          }
          resolve();
        });
      }).finally(() => {
        setView('loaded');
      });
    },
    [activeSearchTerm, sortBy, sortOrder, currentUser]
  );

  const onFilterClick = () => {
    setActiveSearchTerm(searchTerm || null);
    fetchUsers(1, paginationMeta.perPage, searchTerm || null);
  };

  const handlePageChange = (newPage) => {
    fetchUsers(newPage + 1, paginationMeta.perPage); // Convert from 0-indexed to 1-indexed
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    fetchUsers(1, newRowsPerPage);
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    fetchUsers(1, paginationMeta.perPage, activeSearchTerm, newSortBy, newSortOrder);
  };

  const getReadyForNextStepStatus = (readyForNextStep) => {
    if (readyForNextStep === 'yes' || readyForNextStep === 'maybe') {
      return <StatusMessage status='success' message='Yes' isMobile={isMobile} />;
    }

    if (readyForNextStep === 'no') {
      return <StatusMessage status='error' message='No' isMobile={isMobile} />;
    }

    return <StatusMessage status='infoBlue' message='Did not take survey' isMobile={isMobile} />;
  };

  const getReadyForNextStepSortValue = (readyForNextStep) => {
    const statusOrder = {
      yes: 1,
      maybe: 1,
      no: 3,
      no_answer: 4,
    };
    return statusOrder[readyForNextStep] || 5;
  };

  useEffect(() => {
    fetchUsers(1, 10);
  }, []);

  return (
    <div>
      {view === 'loading' && (
        <div className={`${styles.roster_container_wrapper}`}>
          <Skeleton />
          <div className={`${styles.roster_container} container-fluid`}>
            {tableSkeleton((style, item) => (
              <animated.div key={item} style={{ ...style }}>
                <div className={`${styles.roster_row}`}>
                  <div className={`row h-100 ${styles.roster_row_hover}`}>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <svg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M5.56152 10C8.32295 10 10.5615 7.76142 10.5615 5C10.5615 2.23858 8.32295 0 5.56152 0C2.8001 0 0.561523 2.23858 0.561523 5C0.561523 7.76142 2.8001 10 5.56152 10Z'
                          fill='#eee'
                        />
                      </svg>
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col my-auto`}>
                      <Skeleton shape='box' />
                    </div>
                  </div>
                </div>
              </animated.div>
            ))}
          </div>
        </div>
      )}
      {view === 'loaded' && (
        <div className='container marg-bottom-50'>
          <div className='row'>
            <h1 className={`${styles.header} col-lg-6`}>Mentees</h1>
            <div className='col-lg-6' style={{ display: 'flex', alignItems: 'flex-end' }}>
              <NewPatientMenu />
            </div>
          </div>
          <div className={`row ${isMobile ? styles.row_swapped : ''}`}>
            <div className={`material-input ${styles.filter_input_container}`}>
              <input
                type='text'
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onFilterClick();
                  }
                }}
              />
              <button className={`submit ${styles.filter_input_submit}`} onClick={onFilterClick}>
                Search
              </button>
            </div>
            <ExportButton requestExport={getMentorConnectExport} params={{}} />
          </div>
          <div className={`${styles.roster_container}`}>
            {users.length === 0 ? (
              <div className={styles.no_patients_notif_wrap}>
                <div className={styles.no_patients_notif}>
                  <img
                    src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/icons/crying-emoji.jpg'
                    alt='sad face'
                  />
                  <h5>No patients invited!</h5>
                  <p>
                    It doesn't look like any patients have been invited to speak with a mentor. Click
                    <span>+ Invite new patient</span> to invite a new patient.
                  </p>
                </div>
              </div>
            ) : (
              <TableWithPagination
                headers={headers}
                sortableHeaders={sortableHeaders}
                objects={users}
                keys={keys}
                isMobile={isMobile}
                formatting={{
                  created_at: (value) => new Date(value).toLocaleDateString('en'),
                  ready_for_next_step: (value) => ({
                    display: getReadyForNextStepStatus(value),
                    sortValue: getReadyForNextStepSortValue(value),
                  }),
                }}
                serverPagination={{
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handleRowsPerPageChange,
                  onSortChange: handleSortChange,
                  totalCount: paginationMeta.totalCount,
                  currentPage: paginationMeta.currentPage,
                  currentRowsPerPage: paginationMeta.perPage,
                  currentSortBy: sortBy,
                  currentSortOrder: sortOrder,
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
